import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import {
  GET_EMPLOYEE,
  STORE_JOB_ROLES,
  STORE_STAFF_JOB_ROLES_BY_STORE,
  STAFF_JOB_COMPENSATIONS_BY_STORE,
  CREATE_OR_UPDATE_STAFF_JOB_COMPENSATION,
  DELETE_STAFF_JOB_COMPENSATION,
} from "src/constants/queries";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { convertCentsToDollars, convertDollarToCents, formatCentsToDollarsWithDollarSign } from "src/helpers";
import type {
  StoreEmployeeOption,
  StoreJobRole,
  StaffJobCompensation,
  StaffJobCompensationInput,
  StoreStaffJobRole,
} from "../types/payroll.types";

export function CompensationTab() {
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const [employees, setEmployees] = useState<StoreEmployeeOption[]>([]);
  const [jobRoles, setJobRoles] = useState<StoreJobRole[]>([]);
  const [assignments, setAssignments] = useState<StoreStaffJobRole[]>([]);
  const [allCompensations, setAllCompensations] = useState<StaffJobCompensation[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<StaffJobCompensation | null>(null);

  const [assignEmployeeId, setAssignEmployeeId] = useState<number | null>(null);
  const [assignRoleId, setAssignRoleId] = useState<string>("");

  const [form, setForm] = useState<StaffJobCompensationInput>({
    payType: "HOURLY",
    tipEligible: false,
    hourlyRateCents: null,
    salaryCents: null,
    overtimeMultiplier: null,
    tipWeightOverride: null,
  });
  const [hourlyRateDollars, setHourlyRateDollars] = useState<string>("");
  const [salaryDollars, setSalaryDollars] = useState<string>("");

  const employeesWithStaffId = employees.filter((e) => e.storeStaffId != null && e.storeStaffId > 0);

  const employeeNameByStaffId = useMemo(() => {
    const map = new Map<number, string>();
    for (const e of employeesWithStaffId) {
      if (e.storeStaffId == null) continue;
      map.set(e.storeStaffId, [e.firstName, e.lastName].filter(Boolean).join(" ") || e.email || e.correlationId);
    }
    return map;
  }, [employeesWithStaffId]);

  const jobRoleNameById = useMemo(() => {
    const map = new Map<number, string>();
    jobRoles.forEach((j) => map.set(j.id, j.name));
    return map;
  }, [jobRoles]);

  const assignedRoleIdsByStaffId = useMemo(() => {
    const map = new Map<number, number[]>();
    assignments.forEach((a) => {
      const list = map.get(a.storeStaffId) ?? [];
      if (!list.includes(a.storeJobRoleId)) list.push(a.storeJobRoleId);
      map.set(a.storeStaffId, list);
    });
    return map;
  }, [assignments]);

  const assignedRolesForAssignEmployee =
    assignEmployeeId != null ? assignments.filter((a) => a.storeStaffId === assignEmployeeId) : [];
  const compensationRoleIdsForAssignEmployee =
    assignEmployeeId != null
      ? new Set(allCompensations.filter((c) => c.storeStaffId === assignEmployeeId).map((c) => c.storeJobRoleId))
      : new Set<number>();
  const availableRolesToAssign = assignedRolesForAssignEmployee.filter(
    (a) => !compensationRoleIdsForAssignEmployee.has(a.storeJobRoleId)
  );
  const canOpenAdd = assignEmployeeId != null && availableRolesToAssign.length > 0;

  const fetchEmployees = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    try {
      const { data } = await axBe.post<{ data?: { storeEmployeesByStoreCorrelationId?: StoreEmployeeOption[] } }>(
        GRAPHQL_ENDPOINT,
        { query: GET_EMPLOYEE, variables: { storeCorrelationId: hasRestaurant.correlationId } }
      );
      setEmployees(data?.data?.storeEmployeesByStoreCorrelationId ?? []);
    } catch {
      setEmployees([]);
    }
  }, [hasRestaurant?.correlationId, axBe]);

  const fetchJobRoles = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    try {
      const { data } = await axBe.post<{ data?: { storeJobRoles?: StoreJobRole[] } }>(GRAPHQL_ENDPOINT, {
        query: STORE_JOB_ROLES,
        variables: { storeCorrelationId: hasRestaurant.correlationId, activeOnly: true },
      });
      setJobRoles(data?.data?.storeJobRoles ?? []);
    } catch {
      setJobRoles([]);
    }
  }, [hasRestaurant?.correlationId, axBe]);

  const fetchAssignments = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    try {
      const { data } = await axBe.post<{ data?: { storeStaffJobRolesByStore?: StoreStaffJobRole[] } }>(
        GRAPHQL_ENDPOINT,
        {
          query: STORE_STAFF_JOB_ROLES_BY_STORE,
          variables: { storeCorrelationId: hasRestaurant.correlationId },
        }
      );
      setAssignments(data?.data?.storeStaffJobRolesByStore ?? []);
    } catch {
      setAssignments([]);
    }
  }, [hasRestaurant?.correlationId, axBe]);

  const fetchAllCompensations = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    setLoadingList(true);
    setError(null);
    try {
      const { data } = await axBe.post<{ data?: { staffJobCompensationsByStore?: StaffJobCompensation[] } }>(
        GRAPHQL_ENDPOINT,
        {
          query: STAFF_JOB_COMPENSATIONS_BY_STORE,
          variables: { storeCorrelationId: hasRestaurant.correlationId },
        }
      );
      setAllCompensations(data?.data?.staffJobCompensationsByStore ?? []);
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Failed to load compensations");
      setAllCompensations([]);
    } finally {
      setLoadingList(false);
    }
  }, [hasRestaurant?.correlationId, axBe]);

  useEffect(() => {
    fetchEmployees();
    fetchJobRoles();
    fetchAssignments();
    fetchAllCompensations();
  }, [fetchEmployees, fetchJobRoles, fetchAssignments, fetchAllCompensations]);

  const openAdd = () => {
    const roleId = assignRoleId ? parseInt(assignRoleId, 10) : null;
    if (assignEmployeeId == null || !roleId || Number.isNaN(roleId)) return;
    setEditing(null);
    setForm({
      payType: "HOURLY",
      tipEligible: false,
      hourlyRateCents: null,
      salaryCents: null,
      overtimeMultiplier: null,
      tipWeightOverride: null,
    });
    setHourlyRateDollars("");
    setSalaryDollars("");
    setModalOpen(true);
  };

  const openEdit = (c: StaffJobCompensation) => {
    setEditing(c);
    setForm({
      payType: c.payType,
      tipEligible: c.tipEligible,
      hourlyRateCents: c.hourlyRateCents ?? null,
      salaryCents: c.salaryCents ?? null,
      overtimeMultiplier: c.overtimeMultiplier ?? null,
      tipWeightOverride: c.tipWeightOverride ?? null,
    });
    setHourlyRateDollars(
      c.payType === "HOURLY" && c.hourlyRateCents != null ? String(convertCentsToDollars(c.hourlyRateCents)) : ""
    );
    setSalaryDollars(
      c.payType === "SALARY" && c.salaryCents != null ? String(convertCentsToDollars(c.salaryCents)) : ""
    );
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const buildPayloadFromForm = (): StaffJobCompensationInput => {
    if (editing) {
      return { ...form };
    }
    const hourlyCents =
      form.payType === "HOURLY" && hourlyRateDollars.trim() !== ""
        ? convertDollarToCents(hourlyRateDollars)
        : null;
    const salaryCents =
      form.payType === "SALARY" && salaryDollars.trim() !== "" ? convertDollarToCents(salaryDollars) : null;
    return {
      ...form,
      hourlyRateCents: hourlyCents,
      salaryCents: salaryCents,
    };
  };

  const handleSave = async () => {
    const storeStaffId = editing?.storeStaffId ?? assignEmployeeId;
    const storeJobRoleId = editing?.storeJobRoleId ?? (assignRoleId ? parseInt(assignRoleId, 10) : null);
    if (storeStaffId == null || storeJobRoleId == null || Number.isNaN(storeJobRoleId)) return;
    try {
      await axBe.post(GRAPHQL_ENDPOINT, {
        query: CREATE_OR_UPDATE_STAFF_JOB_COMPENSATION,
        variables: { storeStaffId, storeJobRoleId, input: buildPayloadFromForm() },
      });
      closeModal();
      fetchAllCompensations();
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Failed to save");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this compensation?")) return;
    try {
      await axBe.post(GRAPHQL_ENDPOINT, {
        query: DELETE_STAFF_JOB_COMPENSATION,
        variables: { id },
      });
      fetchAllCompensations();
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Failed to delete");
    }
  };

  const jobRoleName = (jobRoleId: number) => jobRoleNameById.get(jobRoleId) ?? String(jobRoleId);

  return (
    <div>
      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      <h3 className="text-sm font-medium text-[#374151] mb-2">Add compensation</h3>
      <p className="text-xs text-[#6b7280] mb-3">
        Select an employee and a job role they’re already assigned. Then add pay for that role.
      </p>
      <div className="flex flex-wrap items-end gap-2 mb-2">
        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1">Employee</label>
          <select
            value={assignEmployeeId ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              setAssignEmployeeId(v ? parseInt(v, 10) : null);
              setAssignRoleId("");
            }}
            className="border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm min-w-[180px]"
          >
            <option value="">Select employee</option>
            {employeesWithStaffId.map((emp) => (
              <option key={emp.correlationId} value={emp.storeStaffId!}>
                {[emp.firstName, emp.lastName].filter(Boolean).join(" ") || emp.email || emp.correlationId}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1">Job role</label>
          <select
            value={assignRoleId}
            onChange={(e) => setAssignRoleId(e.target.value)}
            className="border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm min-w-[140px]"
          >
            <option value="">Select role</option>
            {availableRolesToAssign.map((a) => (
              <option key={a.storeJobRoleId} value={a.storeJobRoleId}>
                {a.jobRoleName ?? jobRoleName(a.storeJobRoleId)}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={openAdd}
          disabled={!canOpenAdd || !assignRoleId}
          className="px-4 py-2 rounded-lg bg-[#FD7657] text-white text-sm font-medium hover:opacity-95 disabled:opacity-50"
        >
          Add compensation
        </button>
      </div>
      {employees.length > 0 && employeesWithStaffId.length === 0 && (
        <p className="text-xs text-amber-600 mb-2">No employees with staff records found for this store.</p>
      )}

      <div className="mt-12">
        <h3 className="text-sm font-semibold text-[#374151] mb-2">Compensations</h3>
        {loadingList ? (
          <p className="text-sm text-[#6b7280]">Loading…</p>
        ) : (
          <div className="rounded-lg border border-[#e5e7eb]">
            <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left border-collapse">
              <thead>
                <tr className="bg-[#f9fafb] border-b border-[#e5e7eb]">
                  <th className="py-3 px-4 text-xs font-medium text-[#6b7280]">Employee</th>
                  <th className="py-3 px-4 text-xs font-medium text-[#6b7280]">Job role</th>
                  <th className="py-3 px-4 text-xs font-medium text-[#6b7280]">Pay type</th>
                  <th className="py-3 px-4 text-xs font-medium text-[#6b7280]">Rate / Salary</th>
                  <th className="py-3 px-4 text-xs font-medium text-[#6b7280]">Tip pools</th>
                  <th className="py-3 px-4 text-xs font-medium text-[#6b7280] whitespace-nowrap w-32 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {allCompensations.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-10 text-center text-[#6b7280] text-sm">
                      No compensations yet. Add one above.
                    </td>
                  </tr>
                ) : (
                  allCompensations.map((c) => (
                    <tr key={c.id} className="border-t border-[#f3f4f6] hover:bg-[#f9fafb]">
                      <td className="py-3 px-4 text-sm font-semibold text-[#111827]">
                        {employeeNameByStaffId.get(c.storeStaffId) ?? `Staff #${c.storeStaffId}`}
                      </td>
                      <td className="py-3 px-4 text-sm text-[#374151]">{jobRoleName(c.storeJobRoleId)}</td>
                      <td className="py-3 px-4 text-xs text-[#6b7280] uppercase tracking-wide">
                        {c.payType === "HOURLY" ? "Hourly" : "Salary"}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <span className="font-semibold text-[#111827]">
                          {c.payType === "HOURLY" && c.hourlyRateCents != null
                            ? `${formatCentsToDollarsWithDollarSign(c.hourlyRateCents)}/hr`
                            : c.payType === "SALARY" && c.salaryCents != null
                              ? `${formatCentsToDollarsWithDollarSign(c.salaryCents)} / year`
                              : "—"}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {c.tipEligible ? (
                          <span className="inline-flex items-center rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                            Eligible for tips
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full border border-[#e5e7eb] bg-[#f9fafb] px-2.5 py-0.5 text-xs font-medium text-[#6b7280]">
                            Not eligible
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap text-right">
                        <div className="inline-flex items-center justify-end gap-3">
                          <button
                            type="button"
                            onClick={() => openEdit(c)}
                            className="text-[#FD7657] text-sm font-medium hover:underline transition-colors duration-150"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(c.id)}
                            className="text-red-600 text-sm font-medium hover:underline transition-colors duration-150"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" role="dialog">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-[#111827] mb-4">
              {editing ? "Edit compensation" : "Add compensation"}
            </h3>
            {!editing && assignEmployeeId != null && assignRoleId && (
              <p className="text-sm text-[#6b7280] mb-4">
                {employeeNameByStaffId.get(assignEmployeeId)} – {jobRoleName(parseInt(assignRoleId, 10))}
              </p>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Pay type</label>
                <select
                  value={form.payType}
                  onChange={(e) =>
                    setForm((f: StaffJobCompensationInput) => ({ ...f, payType: e.target.value as "HOURLY" | "SALARY" }))
                  }
                  className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm"
                >
                  <option value="HOURLY">Hourly</option>
                  <option value="SALARY">Salary</option>
                </select>
              </div>
              {form.payType === "HOURLY" && (
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Hourly rate ($)</label>
                  <input
                    type="number"
                    step={0.01}
                    min={0}
                    value={editing ? (form.hourlyRateCents != null ? convertCentsToDollars(form.hourlyRateCents) : "") : hourlyRateDollars}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (editing) setForm((f) => ({ ...f, hourlyRateCents: v ? convertDollarToCents(v) : null }));
                      else setHourlyRateDollars(v);
                    }}
                    className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g. 15.00"
                  />
                </div>
              )}
              {form.payType === "SALARY" && (
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Salary ($)</label>
                  <input
                    type="number"
                    step={0.01}
                    min={0}
                    value={editing ? (form.salaryCents != null ? convertCentsToDollars(form.salaryCents) : "") : salaryDollars}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (editing) setForm((f) => ({ ...f, salaryCents: v ? convertDollarToCents(v) : null }));
                      else setSalaryDollars(v);
                    }}
                    className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g. 500.00"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Overtime multiplier (optional)</label>
                <input
                  type="number"
                  step={0.1}
                  min={0}
                  value={form.overtimeMultiplier ?? ""}
                  onChange={(e) =>
                    setForm((f: StaffJobCompensationInput) => ({
                      ...f,
                      overtimeMultiplier: e.target.value ? parseFloat(e.target.value) : null,
                    }))
                  }
                  className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm"
                  placeholder="e.g. 1.5"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="tipEligible"
                  checked={form.tipEligible}
                  onChange={(e) => setForm((f: StaffJobCompensationInput) => ({ ...f, tipEligible: e.target.checked }))}
                  className="rounded border-[#e5e7eb]"
                />
                <label htmlFor="tipEligible" className="text-sm text-[#374151]">
                  Eligible for tip pools
                </label>
              </div>
              <p className="text-xs text-[#6b7280] -mt-1">Affects pool distribution; only eligible staff receive allocated tips.</p>
              {form.tipEligible && (
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Tip weight override (optional)</label>
                  <input
                    type="number"
                    min={0}
                    value={form.tipWeightOverride ?? ""}
                    onChange={(e) =>
                      setForm((f: StaffJobCompensationInput) => ({
                        ...f,
                        tipWeightOverride: e.target.value ? parseFloat(e.target.value) : null,
                      }))
                    }
                    className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 rounded-lg border border-[#e5e7eb] text-sm font-medium text-[#374151] hover:bg-[#f9fafb]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={
                  form.payType === "HOURLY"
                    ? (editing ? form.hourlyRateCents == null : !hourlyRateDollars.trim())
                    : (editing ? form.salaryCents == null : !salaryDollars.trim())
                }
                className="px-4 py-2 rounded-lg bg-[#FD7657] text-white text-sm font-medium hover:opacity-95 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
